import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Clock } from 'lucide-react';
import { useDashboardUsage } from '@/lib/clientApi';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { UsageProgressBar } from '@/components/UsageProgressBar';

function formatDateRange(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${new Date(start).toLocaleDateString('en-US', opts)} – ${new Date(end).toLocaleDateString('en-US', opts)}`;
}

export default function DashboardPage() {
  const { data, isLoading, isError } = useDashboardUsage({ refetchInterval: 2000 });

  // M-01: aria-live announcement for screen readers, rate-limited to once per 10s
  const lastAnnounced = useRef(0);
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    if (!data) return;
    const now = Date.now();
    if (now - lastAnnounced.current >= 10_000) {
      setAnnouncement('Usage updated');
      lastAnnounced.current = now;
    }
  }, [data]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="py-6 space-y-4">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-4 w-60" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-6 space-y-4">
            <Skeleton className="h-6 w-32" />
            <div className="grid grid-cols-3 gap-4">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Couldn&apos;t load usage data — try again.</AlertDescription>
      </Alert>
    );
  }

  const d = data;
  const hasOverage = d.overageMinutes > 0;
  const hasEstimatedOverage = !hasOverage && d.estimatedOverageMinutes > 0;
  const usageLabel = hasOverage
    ? `${d.overageMinutes} minutes over your included ${d.includedMinutes}`
    : `${d.usedMinutes} of ${d.includedMinutes} minutes used`;

  return (
    <div className="space-y-6">
      {/* Section 1: Usage Summary */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Current Cycle Usage</CardTitle>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {formatDateRange(d.cycleStart, d.cycleEnd)}
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-block h-2 w-2 rounded-full bg-[#16A34A] animate-pulse"
                    aria-label="Updating live"
                  />
                </TooltipTrigger>
                <TooltipContent>Refreshes every 2 seconds</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span aria-live="polite" aria-atomic="true" className="sr-only">
              {announcement}
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <UsageProgressBar usedMinutes={d.usedMinutes} includedMinutes={d.includedMinutes} />
          <p className="text-sm">{usageLabel}</p>
          {hasOverage && (
            <p className="text-sm font-medium" style={{ color: '#DC2626' }}>
              <AlertCircle className="inline h-4 w-4 mr-1" />
              You are currently {d.overageMinutes} minutes over your included time.
            </p>
          )}
          {hasEstimatedOverage && (
            <p className="text-sm" style={{ color: '#D97706' }}>
              <Clock className="inline h-4 w-4 mr-1" />
              Estimated overage: ~{d.estimatedOverageMinutes} minutes at current pace.
            </p>
          )}
          {!hasOverage && !hasEstimatedOverage && (
            <p className="text-sm text-muted-foreground">No overage at current pace.</p>
          )}
        </CardContent>
      </Card>

      {/* Section 2: This Cycle */}
      <Card>
        <CardHeader>
          <CardTitle>This Cycle</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="text-xl font-semibold tabular-nums">{d.sessionsBillable}</div>
              <div className="text-xs text-muted-foreground mt-1">Billable sessions</div>
            </div>
            <div>
              <div className="text-xl font-semibold tabular-nums text-muted-foreground">
                {d.sessionsNonBillable}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Non-billable sessions</div>
            </div>
            <div>
              <div className="text-xl font-semibold tabular-nums">
                {d.incrementsUsed} / {d.incrementsIncluded}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Increments used / included</div>
            </div>
            {d.overageIncrements > 0 && (
              <div>
                <div className="text-xl font-semibold tabular-nums" style={{ color: '#DC2626' }}>
                  {d.overageIncrements}
                </div>
                <div className="text-xs mt-1" style={{ color: '#DC2626' }}>
                  Overage increments: {d.overageIncrements}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Quick Actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button asChild>
          <Link to="/portal/tickets/new">Submit a ticket</Link>
        </Button>
        <Button asChild variant="ghost">
          <Link to="/portal/tickets">View my tickets</Link>
        </Button>
      </div>

      {/* Section 4: Reports teaser */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <Link to="/portal/reports" className="text-sm text-primary underline">
            View all reports
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
